/* 3WE stretch system
   ---------------------------------------------------------------------------
   The brand's lateral-stretch typography. One engine, four surfaces:

   1. flankstar headings  — ★ WORDS ★ fill the row, ALWAYS one line, with a
                            deliberate margin off the stars and a LOCKED block
                            height so tap-swapped slogans never shift the page
   2. header wordmark     — ELITE NETWORK reaches toward the header icons
                            (big letters come from CSS; only mild lateral
                            stretch here, so the logo never looks deep-fried)
   3. hero heading        — EACH LINE stretched independently to the column
   4. ticker phrases      — every phrase gets its own deterministic stretch

   Plus the star-cut A: every display A in the flank/hero lockups gets the
   Instagram device — a star knocked out of its counter (CSS .threewe-a).

   Hard-won laws:
   - NEVER trust one measurement (iOS paints fallback fonts after
     fonts.ready). Every fit verifies against rendered neighbour boxes.
   - NEVER refit on height-only resizes: iOS fires resize continuously while
     the URL bar collapses during scroll, and reset-measure-apply flashes.
     Width-gated + result-keyed fits make scrolling optically silent.
   - NEVER let a fit change block height after first paint: swapped slogans
     render inside a height LOCKED to the tallest allowed state.
   - The condense guard has NO floor: a too-long line squeezes until it fits;
     overlapping the stars is never an acceptable outcome (measured 2 Aug:
     the 0.9 floor let long slogans overflow straight through the stars).

   Loaded deferred from snippets/3we-flankstar-stretch.liquid. */

(function () {
  'use strict';

  var FLANK_MAX = 3.6; /* max lateral stretch for ★ headings */
  var GROW_MAX = 1.5; /* max letter growth — also the height-lock factor */
  var FILL = 0.94; /* reach 94% of the room: a real margin off the stars */
  var STAR_GAP = 10; /* px the guard insists on between ink and star */
  var HERO_MAX = 2.2;
  var WORDMARK_MAX = 1.2; /* the logo stays a logo */

  var fontsGen = 0; /* bumped when any font finishes loading */

  function hashText(s) {
    var x = 0;
    for (var i = 0; i < s.length; i++) x = (x * 31 + s.charCodeAt(i)) | 0;
    return Math.abs(x);
  }

  /* --------------------------------------------------- star-cut A device */

  /* Wrap every A in a marker span; CSS knocks a canvas-coloured star out of
     the counter. The wrapper is inline with identical advance, so widths
     measured afterwards are unaffected. */
  function starCutA(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var targets = [];
    var n;
    while ((n = walker.nextNode())) {
      if (/[ao]/i.test(n.textContent) && !(n.parentElement && n.parentElement.classList.contains('threewe-a'))) {
        targets.push(n);
      }
    }
    /* Seed and the O ordinal are GLOBAL across the whole lockup, not per
       text node: knock() rebuilds headings word-per-node, and a per-node
       counter made every word's first O position 0 — so the "1 in 3"
       pick degenerated to all-or-nothing depending on the seed. */
    var seed = hashText(root.textContent || '');
    var oIndex = 0;
    /* One star species per line (owner, 2 Aug): if any red O in this
       lockup will carry a star, no A gets one — the two stars are sized
       differently and mixing them in one line reads as a mistake. */
    var redO = targets.some(function (node) {
      return /[oO]/.test(node.textContent) && !!(node.parentElement && node.parentElement.closest('em'));
    });
    targets.forEach(function (node) {
      var frag = document.createDocumentFragment();
      /* O's carry a big star ONLY inside red text (the em knock word) —
         owner, 2 Aug: "big star in an O if it's in red text… only in
         VISION, not NOW". White O's stay plain; every red O is starred,
         with every second one upside down. */
      var inRed = !!(node.parentElement && node.parentElement.closest('em'));
      node.textContent.split(/([aoAO])/).forEach(function (part) {
        if (/^[aA]$/.test(part)) {
          frag.appendChild(redO ? document.createTextNode(part) : makeStarLetter(part, 'a', false));
        } else if (/^[oO]$/.test(part)) {
          oIndex++;
          /* Upright only, for now — owner call 2 Aug. */
          frag.appendChild(inRed ? makeStarLetter(part, 'o', false) : document.createTextNode(part));
        } else if (part) {
          frag.appendChild(document.createTextNode(part));
        }
      });
      node.parentNode.replaceChild(frag, node);
    });
  }

  function makeStarLetter(ch, kind, flip) {
    var a = document.createElement('span');
    a.className = 'threewe-a' + (kind === 'o' ? ' threewe-a--o' : '') + (flip ? ' threewe-a--flip' : '');
    a.textContent = ch;
    var star = document.createElement('span');
    star.className = 'threewe-a__star';
    star.setAttribute('aria-hidden', 'true');
    a.appendChild(star);
    return a;
  }

  /* Publish the baseline's offset from the box top (px) as --threewe-bl
     on the lockup root: the star-letter patches position in exact
     canvas-measured em-above-baseline coordinates, and the baseline's
     place inside the line box depends on line-height, which differs per
     surface. A zero-size inline-block probe sits ON the baseline. */
  function setBaselineVar(el) {
    var probe = document.createElement('span');
    probe.style.cssText = 'display:inline-block;width:0;height:0;padding:0;margin:0;border:0;vertical-align:baseline;';
    el.appendChild(probe);
    var bl = probe.getBoundingClientRect().top - el.getBoundingClientRect().top;
    el.removeChild(probe);
    if (bl > 0) el.style.setProperty('--threewe-bl', bl.toFixed(2) + 'px');
  }

  /* THIS engine's own ink centre for a glyph, in px from the text origin —
     measureText().actualBoundingBox* is the renderer reporting where it
     actually paints. Hardcoded metrics measured in one browser were 1-2%
     off in another (owner pixel-measured the drift on iOS); asking each
     engine at runtime is the only anchor that is true everywhere. */
  var inkCtx = null;
  var inkCache = {};
  function setInkCentres(root) {
    root.querySelectorAll('.threewe-a').forEach(function (a) {
      var ch = (a.textContent || '')[0];
      if (!ch) return;
      var cs = getComputedStyle(a);
      var font = cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
      var key = ch + '|' + font;
      if (!(key in inkCache)) {
        if (!inkCtx) inkCtx = document.createElement('canvas').getContext('2d');
        inkCtx.font = font;
        var m = inkCtx.measureText(ch);
        if (typeof m.actualBoundingBoxRight !== 'number') return; /* ancient engine: keep CSS fallback */
        /* ink spans [-abbLeft, +abbRight] around the origin → centre: */
        inkCache[key] = (m.actualBoundingBoxRight - m.actualBoundingBoxLeft) / 2;
      }
      a.style.setProperty('--threewe-cx', inkCache[key].toFixed(2) + 'px');
    });
  }

  /* ------------------------------------------------- 1. flankstar headings */

  function ink(span) {
    var k = span.querySelector('.threewe-flankstar__ink');
    if (!k) {
      k = document.createElement('span');
      k.className = 'threewe-flankstar__ink';
      /* Absolutely centred on the H2 (which is position:relative in CSS):
         symmetric by construction, immune to flex shrink/overflow shifting
         the words off-centre on long swapped slogans (measured 2 Aug:
         over-wide lines overflowed their flex slot rightward and stretched
         into the right star). Layout box is zero-impact; the h2's locked
         min-height carries the block. */
      k.style.position = 'absolute';
      k.style.left = '50%';
      k.style.top = '50%';
      k.style.whiteSpace = 'nowrap'; /* one line, always — condense, never wrap */
      k.style.transform = 'translate(-50%, -50%)';
      k.style.transformOrigin = 'center';
      while (span.firstChild) k.appendChild(span.firstChild);
      span.appendChild(k);
    }
    return k;
  }

  function knock(span) {
    var k = ink(span);
    k.querySelectorAll('.threewe-a').forEach(function (a) {
      a.replaceWith(document.createTextNode(a.textContent));
    });
    k.querySelectorAll('em[data-threewe-knock]').forEach(function (e) {
      e.replaceWith(e.textContent);
    });
    k.normalize();
    if (!k.children.length) {
      var words = k.textContent.trim().split(/\s+/);
      if (words.length >= 2) {
        var idx = -1;
        var len = 0;
        for (var i = 1; i < words.length; i++) {
          if (words[i].length >= len) {
            len = words[i].length;
            idx = i;
          }
        }
        k.textContent = '';
        words.forEach(function (w, j) {
          if (j) k.append(' ');
          if (j === idx) {
            var em = document.createElement('em');
            em.setAttribute('data-threewe-knock', '');
            em.textContent = w;
            k.append(em);
          } else {
            k.append(w);
          }
        });
      }
    }
    starCutA(k);
  }

  function fitFlank(h) {
    var text = h.querySelector('.threewe-flankstar__text');
    if (!text) return;
    var k = ink(text);

    /* Height lock: reserve the tallest state ONCE, before any fitting, so
       tap-swapped slogans of any grow level share one block height and the
       page never shifts. */
    if (!h.dataset.threeweBaseFs) {
      h.style.fontSize = '';
      var cs0 = getComputedStyle(h);
      var fs0 = parseFloat(cs0.fontSize);
      var lh0 = cs0.lineHeight === 'normal' ? fs0 * 1.2 : parseFloat(cs0.lineHeight);
      h.dataset.threeweBaseFs = fs0;
      h.style.minHeight = Math.ceil(lh0 * GROW_MAX) + 'px';
    }
    var baseFs = parseFloat(h.dataset.threeweBaseFs);

    /* Skip identical work — this is what makes iOS scrolling silent. */
    var key = k.textContent + '|' + window.innerWidth + '|' + fontsGen;
    if (h.dataset.threeweKey === key) return;

    var items = [].slice.call(h.children).filter(function (c) {
      return getComputedStyle(c).display !== 'none';
    });
    var stars = items.filter(function (c) {
      return c !== text;
    });

    /* Natural (unscaled) ink width, cached per content+font: rects animate
       under the lerp transition, so mid-transition reads divide wrong — a
       cached measurement taken at scale 1 never lies. A fresh ink (new
       slogan) always starts unscaled, so its first read is exact. */
    function naturalWidth(fontPx) {
      var nKey = k.textContent + '|' + fontPx;
      if (k.dataset.natKey === nKey) return parseFloat(k.dataset.natW);
      /* scrollWidth is pure layout: immune to the ink's own scaleX AND to
         the slogan engine's swap animation transforming the parent span
         mid-measurement (rect-based reads came in ~7% short during the
         190ms swap and every post-swap fit overshot into the stars). */
      var w = k.scrollWidth;
      k.dataset.natKey = nKey;
      k.dataset.natW = w;
      return w;
    }

    function availNow() {
      var gap = parseFloat(getComputedStyle(h).columnGap) || 0;
      var starsW = 0;
      stars.forEach(function (c) {
        starsW += c.getBoundingClientRect().width;
      });
      return h.clientWidth - starsW - gap * Math.max(0, items.length - 1);
    }

    h.style.fontSize = '';
    var w = naturalWidth(baseFs);
    var avail = availNow();
    if (!w || avail <= 0) return;

    var need = (avail * FILL) / w;
    if (need > FLANK_MAX) {
      /* Short phrase: grow the letters (within the locked height), then
         stretch the remainder. */
      var grow = Math.min(need / FLANK_MAX, GROW_MAX);
      if (w * grow < avail * 0.95) {
        h.style.fontSize = (baseFs * grow).toFixed(1) + 'px';
        w = naturalWidth(baseFs * grow);
        avail = availNow();
        if (!w || avail <= 0) return;
      }
    }
    var scale = Math.min((avail * FILL) / w, FLANK_MAX);

    /* PREDICTIVE star guard — computed from known widths, never from
       animated rects: the ink is centred on the h2, so its final edges are
       centre ± (w × scale)/2. Clamp the scale so those edges respect the
       gap to the rendered star boxes. No lower bound: fitting always wins. */
    if (stars.length > 1) {
      var hr = h.getBoundingClientRect();
      var centre = hr.left + hr.width / 2;
      var rects = stars
        .map(function (c) {
          return c.getBoundingClientRect();
        })
        .filter(function (r) {
          return r.width > 0;
        })
        .sort(function (a, b) {
          return a.left - b.left;
        });
      if (rects.length > 1) {
        var room = Math.min(centre - rects[0].right, rects[rects.length - 1].left - centre) - STAR_GAP;
        if (room > 0) scale = Math.min(scale, (room * 2) / w);
      }
    }

    /* A LIVE refit (same text, new viewport) re-rasterizes the ink: the
       compositor shows a scaled — blurry — texture, then snaps sharp.
       Costume that seam with the brand tear (shadow-only, one shot). */
    var prevSx = k.style.getPropertyValue('--threewe-sx');
    if (h.dataset.threeweKey && prevSx && Math.abs(parseFloat(prevSx) - scale) > 0.03) {
      k.classList.remove('threewe-resnap');
      void k.offsetWidth;
      k.classList.add('threewe-resnap');
      clearTimeout(k._threeweResnapT);
      k._threeweResnapT = setTimeout(function () {
        k.classList.remove('threewe-resnap');
      }, 320);
    }

    k.style.transform = 'translate(-50%, -50%) scaleX(' + scale.toFixed(3) + ')';
    k.style.setProperty('--threewe-sx', scale.toFixed(3));
    k.style.setProperty('--threewe-inv', (1 / scale).toFixed(4));
    setBaselineVar(k);
    setInkCentres(k);
    h.classList.add('threewe-fitted');
    h.dataset.threeweKey = key;
  }

  /* ---------------------------------------------------- 2. header wordmark */

  function fitWordmark() {
    document.querySelectorAll('.threewe-wordmark').forEach(function (wm) {
      var mark = wm.querySelector('.threewe-wordmark__mark');
      var k = wm.querySelector('.threewe-stretch-ink');
      if (!k) {
        k = document.createElement('span');
        k.className = 'threewe-stretch-ink';
        k.style.display = 'inline-block';
        while (wm.firstChild && wm.firstChild !== mark) k.appendChild(wm.firstChild);
        wm.insertBefore(k, mark || null);
      }
      var key = 'wm|' + window.innerWidth + '|' + fontsGen;
      if (wm.dataset.threeweKey === key) return;
      var curS = parseFloat(k.style.getPropertyValue('--threewe-sx')) || 1;
      var w = k.getBoundingClientRect().width / curS;
      if (!w) return;
      var header = wm.closest('header') || wm.closest('.header-wrapper');
      var icons = header && header.querySelector('.header__icons');
      if (!icons) return;
      var kr = k.getBoundingClientRect();
      var markW = mark ? mark.getBoundingClientRect().width : 0;
      var markGap = mark ? 10 : 0;
      var avail = icons.getBoundingClientRect().left - 20 - kr.left - markW - markGap;
      if (avail <= 0) return;
      var s = Math.max(1, Math.min(avail / w, WORDMARK_MAX));
      k.style.transformOrigin = 'left center';
      k.style.transform = s > 1.02 ? 'scaleX(' + s.toFixed(3) + ')' : '';
      k.style.setProperty('--threewe-sx', s.toFixed(3));
      k.style.setProperty('--threewe-inv', (1 / s).toFixed(4));
      if (mark) mark.style.transform = s > 1.02 ? 'translateX(' + ((s - 1) * w).toFixed(1) + 'px)' : '';
      var edge = (mark || k).getBoundingClientRect().right;
      if (edge > icons.getBoundingClientRect().left - 12) {
        k.style.transform = '';
        k.style.setProperty('--threewe-sx', '1');
        k.style.setProperty('--threewe-inv', '1');
        if (mark) mark.style.transform = '';
      }
      wm.dataset.threeweKey = key;
    });
  }

  /* ------------------------------------------------ 3. hero, line by line */

  function fitHero() {
    document.querySelectorAll('.threewe-hero__heading').forEach(function (h) {
      var foreign = [].slice.call(h.querySelectorAll('*')).some(function (el) {
        return !/^(EM|BR|SPAN)$/.test(el.tagName);
      });
      if (foreign && !h.dataset.threeweOriginal) return;
      if (!h.dataset.threeweOriginal) h.dataset.threeweOriginal = h.innerHTML;
      var key = 'hero|' + window.innerWidth + '|' + fontsGen;
      if (h.dataset.threeweKey === key) return;
      h.innerHTML = h.dataset.threeweOriginal;

      var frag = document.createDocumentFragment();
      (function wordify(node, inEm) {
        if (node.nodeType === 3) {
          node.textContent.split(/(\s+)/).forEach(function (part) {
            if (!part) return;
            if (/^\s+$/.test(part)) return frag.appendChild(document.createTextNode(' '));
            var s = document.createElement(inEm ? 'em' : 'span');
            s.className = 'threewe-w';
            s.textContent = part;
            frag.appendChild(s);
          });
        } else if (node.nodeType === 1) {
          if (node.tagName === 'BR') return frag.appendChild(document.createElement('br'));
          var em = inEm || node.tagName === 'EM';
          [].slice.call(node.childNodes).forEach(function (c) {
            wordify(c, em);
          });
        }
      })({ nodeType: 1, tagName: 'X', childNodes: h.childNodes }, false);
      h.textContent = '';
      h.appendChild(frag);

      var words = [].slice.call(h.querySelectorAll('.threewe-w'));
      if (!words.length) return;
      var lines = [];
      var cur = [];
      var curTop = null;
      words.forEach(function (el) {
        var t = el.getBoundingClientRect().top;
        if (curTop === null || Math.abs(t - curTop) < 8) {
          cur.push(el);
          if (curTop === null) curTop = t;
        } else {
          lines.push(cur);
          cur = [el];
          curTop = t;
        }
      });
      lines.push(cur);

      var width = h.clientWidth;
      h.textContent = '';
      lines.forEach(function (lineWords) {
        /* Block lines at max-content stack at the heading's own line-height
           — same height as the natural wrap they replace. */
        var k = document.createElement('span');
        k.style.display = 'block';
        k.style.width = 'max-content';
        k.style.transformOrigin = 'left center';
        lineWords.forEach(function (el, i) {
          if (i) k.appendChild(document.createTextNode(' '));
          k.appendChild(el);
        });
        h.appendChild(k);
        starCutA(k);
        var nw = k.getBoundingClientRect().width;
        if (!nw) return;
        var s = Math.max(1, Math.min((width * 0.995) / nw, HERO_MAX));
        if (s > 1.01) k.style.transform = 'scaleX(' + s.toFixed(3) + ')';
        k.style.setProperty('--threewe-sx', s.toFixed(3));
        k.style.setProperty('--threewe-inv', (1 / s).toFixed(4));
        setBaselineVar(k);
        setInkCentres(k);
      });
      h.classList.add('threewe-fitted');
      h.dataset.threeweKey = key;
    });
  }

  /* ------------------------------------------------------ 4. ticker items */

  function fitTicker() {
    document.querySelectorAll('.threewe-ticker__item').forEach(function (item) {
      if (item.dataset.threeweKey === 'done|' + fontsGen) return;
      item.style.transform = '';
      item.style.marginLeft = '';
      item.style.marginRight = '';
      var w = item.getBoundingClientRect().width;
      if (!w) return;
      /* 1.15–1.8, hashed from the phrase: extreme, varied, and identical
         across both cloned sets so the -50% loop stays seamless. Margins
         reserve the overhang so neighbours never overlap. */
      var s = 1.15 + (hashText(item.textContent.trim()) % 66) / 100;
      var pad = ((s - 1) * w) / 2;
      item.style.display = 'inline-block';
      item.style.transformOrigin = 'center';
      item.style.transform = 'scaleX(' + s.toFixed(3) + ')';
      item.style.marginLeft = pad.toFixed(1) + 'px';
      item.style.marginRight = pad.toFixed(1) + 'px';
      item.dataset.threeweKey = 'done|' + fontsGen;
    });
  }

  /* -------------------------------------------------------- orchestration */

  function fitAll() {
    document.querySelectorAll('h2.threewe-flankstar').forEach(fitFlank);
    fitWordmark();
    fitHero();
    fitTicker();
  }

  function init() {
    var ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    ready.then(function () {
      document.querySelectorAll('h2.threewe-flankstar').forEach(function (h) {
        var text = h.querySelector('.threewe-flankstar__text');
        if (!text) return;
        knock(text);
        fitFlank(h);
        var mo = new MutationObserver(function () {
          mo.disconnect();
          knock(text);
          delete h.dataset.threeweKey;
          fitFlank(h);
          mo.observe(text, { childList: true, characterData: true, subtree: true });
        });
        mo.observe(text, { childList: true, characterData: true, subtree: true });
      });
      fitWordmark();
      fitHero();
      fitTicker();

      /* Width-gated: iOS fires resize for URL-bar height changes on every
         scroll; those must not refit (the reset flash reads as snapping). */
      var lastVW = window.innerWidth;
      var raf = 0;
      addEventListener('resize', function () {
        if (window.innerWidth === lastVW) return;
        lastVW = window.innerWidth;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(fitAll);
      });
      addEventListener('orientationchange', fitAll);
      addEventListener('load', fitAll);
      addEventListener('pageshow', fitAll);
      document.addEventListener('visibilitychange', function () {
        if (!document.hidden) fitAll();
      });
      if (document.fonts && document.fonts.addEventListener) {
        document.fonts.addEventListener('loadingdone', function () {
          fontsGen++;
          fitAll();
        });
      }
      if ('ResizeObserver' in window) {
        var lastW = document.body.clientWidth;
        new ResizeObserver(function () {
          var bw = document.body.clientWidth;
          if (Math.abs(bw - lastW) > 1) {
            lastW = bw;
            fitAll();
          }
        }).observe(document.body);
      }
      setTimeout(fitAll, 800);
      setTimeout(fitAll, 2500);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
